import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { ref, onDisconnect, set, onValue, serverTimestamp } from 'firebase/database';
import { rtdb } from '../lib/firebase';
import { useAuth } from './AuthContext';

interface PresenceContextType {
  isOnline: boolean;
  setPresence: (roomCode: string) => Promise<void>;
  clearPresence: () => Promise<void>;
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (context === undefined) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};

interface PresenceProviderProps {
  children: React.ReactNode;
}

/**
 * Presence system built on the canonical Firebase RTDB pattern:
 *   - `.info/connected` tells us when the RTDB socket is actually alive.
 *   - `onDisconnect()` is registered *before* we mark ourselves online, so the
 *     server authoritatively marks us offline the moment the socket drops
 *     (tab closed, network lost, laptop asleep) — no client heartbeat needed.
 *   - When the socket reconnects (e.g. tab returns to the foreground), the
 *     `.info/connected` listener fires again and we re-register the disconnect
 *     handler and re-assert online. This is what makes backgrounding a tab NOT
 *     mark the player as permanently disconnected.
 *
 * There is deliberately NO setInterval heartbeat: background tabs throttle
 * timers, which was the original cause of players flickering to "offline".
 */
export const PresenceProvider: React.FC<PresenceProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  // The room we are currently keeping presence for. Changing this (re)starts
  // the `.info/connected` effect below.
  const [activeRoom, setActiveRoom] = useState<string | null>(null);
  const activeRoomRef = useRef<string | null>(null);
  const { user } = useAuth();

  // Track browser connectivity purely for UI hints.
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Core presence loop: bound to (user, activeRoom). Because pages within the
  // same room call setPresence with the same code, activeRoom does not change
  // as the user navigates lobby -> game -> results, so this effect stays put
  // and presence never flickers between screens.
  useEffect(() => {
    if (!user?.uid || !activeRoom) return;

    const uid = user.uid;
    const roomCode = activeRoom;
    const presenceRef = ref(rtdb, `status/rooms/${roomCode}/players/${uid}`);
    const connectedRef = ref(rtdb, '.info/connected');
    let cancelled = false;

    const unsubscribe = onValue(connectedRef, async (snap) => {
      if (snap.val() !== true) return; // socket not (yet) connected
      try {
        // 1) Register the server-side disconnect handler FIRST, so there is no
        //    window where we are online without a disconnect hook.
        await onDisconnect(presenceRef).set({
          state: 'offline',
          lastSeen: serverTimestamp(),
        });
        if (cancelled) return;
        // 2) Now assert we are online.
        await set(presenceRef, {
          state: 'online',
          lastSeen: serverTimestamp(),
        });
      } catch (error) {
        console.error('Error establishing presence:', error);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user?.uid, activeRoom]);

  const setPresence = useCallback(async (roomCode: string) => {
    if (!user?.uid) return;
    activeRoomRef.current = roomCode;
    // Triggers / keeps the presence effect running for this room.
    setActiveRoom((prev) => (prev === roomCode ? prev : roomCode));
  }, [user?.uid]);

  const clearPresence = useCallback(async () => {
    const roomCode = activeRoomRef.current;
    if (!user?.uid || !roomCode) {
      setActiveRoom(null);
      activeRoomRef.current = null;
      return;
    }

    const presenceRef = ref(rtdb, `status/rooms/${roomCode}/players/${user.uid}`);
    try {
      // We are leaving on purpose: cancel the disconnect hook and go offline now.
      await onDisconnect(presenceRef).cancel();
      await set(presenceRef, {
        state: 'offline',
        lastSeen: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error clearing presence:', error);
    } finally {
      activeRoomRef.current = null;
      setActiveRoom(null);
    }
  }, [user?.uid]);

  const value: PresenceContextType = {
    isOnline,
    setPresence,
    clearPresence,
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};
